import React, { useEffect, useState } from 'react';
import Container from '@mui/material/Container';
import Typography from '@mui/material/Typography';
import Grid from '@mui/material/Grid';
import Paper from '@mui/material/Paper';
import Stack from '@mui/material/Stack';
import Box from '@mui/material/Box';
import Chip from '@mui/material/Chip';
import Divider from '@mui/material/Divider';
import CircularProgress from '@mui/material/CircularProgress';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { Recipe } from '@/types';
import { RecipeService } from '@/services/RecipeService';
import { useAuth } from '@/context/AuthContext';
import { useCredits } from '@/context/CreditsContext';
import { useI18n } from '@/i18n/I18nContext';
import { getBrewfatherCredentials } from '@/services/BrewfatherService';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import IconButton from '@mui/material/IconButton';
import LocalBarIcon from '@mui/icons-material/LocalBar';
import Button from '@mui/material/Button';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableContainer from '@mui/material/TableContainer';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import WhatshotIcon from '@mui/icons-material/Whatshot';
import GrainIcon from '@mui/icons-material/Grain';
import WaterDropIcon from '@mui/icons-material/WaterDrop';
import AIChatOverlay, { ChatMessage, AI_CHAT_USE_FOOTER, AI_CHAT_FOOTER_BAR_HEIGHT } from '@/components/AIChatOverlay';
import CreditsDisplay from '@/components/CreditsDisplay';
import { AIService } from '@/services/AIService';

/**
 * Get recipe-specific suggested questions for the AI chat
 */
const getSuggestedQuestionsForRecipe = (recipe: Recipe): string[] => {
	const questions: string[] = [
		'What can you tell me about this recipe?',
		'How should I brew this recipe?',
		'What equipment do I need for this recipe?',
		'What should I expect from this beer style?',
		'Are there any tips for brewing this recipe?'
	];

	if (recipe.ingredients?.hops && recipe.ingredients.hops.length > 0) {
		questions.push('What role do the hops play in this recipe?');
		questions.push('How should I time my hop additions?');
	}

	if (recipe.ingredients?.fermentables && recipe.ingredients.fermentables.length > 0) {
		questions.push('What do these fermentables contribute to the beer?');
		questions.push('Can I substitute any of these malts?');
	}

	if (recipe.mashSteps && recipe.mashSteps.length > 0) {
		questions.push('What is the purpose of these mash steps?');
		questions.push('How do I execute the mash schedule?');
	}

	if (recipe.abv !== undefined) {
		questions.push(`How do I achieve the target ABV of ${recipe.abv.toFixed(1)}%?`);
	}

	if (recipe.ibu !== undefined) {
		questions.push(`How do I achieve the target IBU of ${recipe.ibu}?`);
	}

	return questions.slice(0, 8); // Limit to 8 questions
};

const RecipeDetailPage: React.FC = () => {
	const { id } = useParams<{ id: string }>();
	const navigate = useNavigate();
	const { user, getAuthHeader, getUserId } = useAuth();
	const { setCreditsLoading, loadCredits } = useCredits();
	const { t } = useI18n();
	const [recipe, setRecipe] = useState<Recipe | null>(null);
	const [loading, setLoading] = useState(true);

	useEffect(() => {
		if (!user || !id) {
			navigate('/recipes');
			return;
		}
		loadRecipe();
	}, [id, user]);

	const loadRecipe = async () => {
		try {
			setLoading(true);
			const userId = getUserId();
			if (!userId) {
				navigate('/recipes');
				return;
			}
			const authHeader = getAuthHeader();
			const credentials = await getBrewfatherCredentials(userId, authHeader);
			if (!credentials) {
				navigate('/recipes');
				return;
			}
			const response = await RecipeService.getRecipe(id!, userId, authHeader);
			setRecipe(response.item);
		} catch (error: any) {
			console.error('Error loading recipe:', error);
			navigate('/recipes');
		} finally {
			setLoading(false);
		}
	};

	const isNumber = (value: number | null | undefined): value is number => {
		return value !== null && value !== undefined && typeof value === 'number';
	};

	const formatUnit = (amount: number, unit: string): string => {
		return `${amount.toFixed(unit === 'g' || unit === 'oz' ? 1 : 2)} ${unit}`;
	};

	const formatTime = (time: number, use: string): string => {
		if (use === 'dry hop') {
			return `${time} ${time === 1 ? 'day' : 'days'}`;
		}
		return `${time} min`;
	};

	if (loading) {
		return (
			<Container sx={{ py: 8, textAlign: 'center' }}>
				<CircularProgress />
			</Container>
		);
	}

	if (!recipe) {
		return (
			<Container sx={{ py: 8, textAlign: 'center' }}>
				<Typography variant="h6" color="text.secondary">
					Recipe not found
				</Typography>
				<Button onClick={() => navigate('/recipes')} sx={{ mt: 2 }}>
					Back to Recipes
				</Button>
			</Container>
		);
	}

	const typeLabel = t.recipes.type[recipe.type] || recipe.type;

	return (
		<Container 
			maxWidth="lg" 
			sx={{ 
				py: 4,
				...(AI_CHAT_USE_FOOTER && { pb: AI_CHAT_FOOTER_BAR_HEIGHT + 24 })
			}}
		>
			<Box sx={{ mb: 3, display: 'flex', alignItems: 'center', gap: 2 }}>
				<IconButton onClick={() => navigate('/recipes')}>
					<ArrowBackIcon />
				</IconButton>
				<Typography variant="h4" fontWeight={800}>
					{recipe.name}
				</Typography>
				<Chip
					label={typeLabel}
					color="primary"
				/>
				{recipe.style && (
					<Chip
						label={recipe.style}
						variant="outlined"
					/>
				)}
			</Box>

			<Grid container spacing={3}>
				<Grid item xs={12} md={8}>
					{recipe.description && (
						<Paper sx={{ p: 3, mb: 3 }}>
							<Typography variant="h6" fontWeight={700} sx={{ mb: 2 }}>
								Description
							</Typography>
							<Typography variant="body1" color="text.secondary">
								{recipe.description}
							</Typography>
						</Paper>
					)}

					<Paper sx={{ p: 3, mb: 3 }}>
						<Typography variant="h6" fontWeight={700} sx={{ mb: 2 }}>
							Recipe Specifications
						</Typography>
						<Grid container spacing={2}>
							{isNumber(recipe.originalGravity) && (
								<Grid item xs={6} sm={4}>
									<Typography variant="caption" color="text.secondary">
										Original Gravity (OG)
									</Typography>
									<Typography variant="h6" fontWeight={700}>
										{recipe.originalGravity.toFixed(3)}
									</Typography>
								</Grid>
							)}
							{isNumber(recipe.finalGravity) && (
								<Grid item xs={6} sm={4}>
									<Typography variant="caption" color="text.secondary">
										Final Gravity (FG)
									</Typography>
									<Typography variant="h6" fontWeight={700}>
										{recipe.finalGravity.toFixed(3)}
									</Typography>
								</Grid>
							)}
							{isNumber(recipe.abv) && (
								<Grid item xs={6} sm={4}>
									<Typography variant="caption" color="text.secondary">
										ABV
									</Typography>
									<Typography variant="h6" fontWeight={700}>
										{recipe.abv.toFixed(1)}%
									</Typography>
								</Grid>
							)}
							{isNumber(recipe.ibu) && (
								<Grid item xs={6} sm={4}>
									<Typography variant="caption" color="text.secondary">
										IBU
									</Typography>
									<Typography variant="h6" fontWeight={700}>
										{recipe.ibu}
									</Typography>
								</Grid>
							)}
							{isNumber(recipe.srm) && (
								<Grid item xs={6} sm={4}>
									<Typography variant="caption" color="text.secondary">
										SRM
									</Typography>
									<Typography variant="h6" fontWeight={700}>
										{recipe.srm.toFixed(1)}
									</Typography>
								</Grid>
							)}
						</Grid>
					</Paper>

					{recipe.ingredients && (
						<>
							{recipe.ingredients.fermentables && recipe.ingredients.fermentables.length > 0 && (
								<Paper sx={{ p: 3, mb: 3 }}>
									<Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
										<GrainIcon color="primary" />
										<Typography variant="h6" fontWeight={700}>
											Fermentables
										</Typography>
									</Box>
									<TableContainer>
										<Table size="small">
											<TableHead>
												<TableRow>
													<TableCell>Name</TableCell>
													<TableCell align="right">Amount</TableCell>
												</TableRow>
											</TableHead>
											<TableBody>
												{recipe.ingredients.fermentables.map((fermentable, index) => (
													<TableRow key={index}>
														<TableCell>{fermentable.name}</TableCell>
														<TableCell align="right">
															{formatUnit(fermentable.amount, fermentable.unit)}
														</TableCell>
													</TableRow>
												))}
											</TableBody>
										</Table>
									</TableContainer>
								</Paper>
							)}

							{recipe.ingredients.hops && recipe.ingredients.hops.length > 0 && (
								<Paper sx={{ p: 3, mb: 3 }}>
									<Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
										<WhatshotIcon color="primary" />
										<Typography variant="h6" fontWeight={700}>
											Hops
										</Typography>
									</Box>
									<TableContainer>
										<Table size="small">
											<TableHead>
												<TableRow>
													<TableCell>Name</TableCell>
													<TableCell align="right">Amount</TableCell>
													<TableCell align="right">Time</TableCell>
													<TableCell align="right">Use</TableCell>
												</TableRow>
											</TableHead>
											<TableBody>
												{recipe.ingredients.hops.map((hop, index) => (
													<TableRow key={index}>
														<TableCell>{hop.name}</TableCell>
														<TableCell align="right">
															{formatUnit(hop.amount, hop.unit)}
														</TableCell>
														<TableCell align="right">
															{formatTime(hop.time, hop.use)}
														</TableCell>
														<TableCell align="right">
															<Chip 
																label={hop.use} 
																size="small" 
																variant="outlined"
															/>
														</TableCell>
													</TableRow>
												))}
											</TableBody>
										</Table>
									</TableContainer>
								</Paper>
							)}

							{recipe.ingredients.yeast && recipe.ingredients.yeast.length > 0 && (
								<Paper sx={{ p: 3, mb: 3 }}>
									<Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
										<LocalBarIcon color="primary" />
										<Typography variant="h6" fontWeight={700}>
											Yeast
										</Typography>
									</Box>
									<TableContainer>
										<Table size="small">
											<TableHead>
												<TableRow>
													<TableCell>Name</TableCell>
													{recipe.ingredients.yeast.some(y => y.amount !== undefined) && (
														<TableCell align="right">Amount</TableCell>
													)}
												</TableRow>
											</TableHead>
											<TableBody>
												{recipe.ingredients.yeast.map((yeast, index) => (
													<TableRow key={index}>
														<TableCell>{yeast.name}</TableCell>
														{yeast.amount !== undefined && (
															<TableCell align="right">
																{yeast.unit 
																	? `${yeast.amount} ${yeast.unit}`
																	: yeast.amount.toString()
																}
															</TableCell>
														)}
													</TableRow>
												))}
											</TableBody>
										</Table>
									</TableContainer>
								</Paper>
							)}

							{recipe.ingredients.other && recipe.ingredients.other.length > 0 && (
								<Paper sx={{ p: 3, mb: 3 }}>
									<Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
										<WaterDropIcon color="primary" />
										<Typography variant="h6" fontWeight={700}>
											Other Ingredients
										</Typography>
									</Box>
									<TableContainer>
										<Table size="small">
											<TableHead>
												<TableRow>
													<TableCell>Name</TableCell>
													<TableCell align="right">Amount</TableCell>
													<TableCell align="right">Use</TableCell>
												</TableRow>
											</TableHead>
											<TableBody>
												{recipe.ingredients.other.map((other, index) => (
													<TableRow key={index}>
														<TableCell>{other.name}</TableCell>
														<TableCell align="right">
															{formatUnit(other.amount, other.unit)}
														</TableCell>
														<TableCell align="right">{other.use}</TableCell>
													</TableRow>
												))}
											</TableBody>
										</Table>
									</TableContainer>
								</Paper>
							)}
						</>
					)}

					{recipe.mashSteps && recipe.mashSteps.length > 0 && (
						<Paper sx={{ p: 3 }}>
							<Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
								<AccessTimeIcon color="primary" />
								<Typography variant="h6" fontWeight={700}>
									Mash Steps
								</Typography>
							</Box>
							<TableContainer>
								<Table size="small">
									<TableHead>
										<TableRow>
											<TableCell>Step</TableCell>
											<TableCell align="right">Temperature</TableCell>
											<TableCell align="right">Duration</TableCell>
										</TableRow>
									</TableHead>
									<TableBody>
										{recipe.mashSteps.map((step, index) => (
											<TableRow key={index}>
												<TableCell>{step.name || `Step ${index + 1}`}</TableCell>
												<TableCell align="right">
													{step.temperature}°C
												</TableCell>
												<TableCell align="right">
													{step.duration} min
												</TableCell>
											</TableRow>
										))}
									</TableBody>
								</Table>
							</TableContainer>
						</Paper>
					)}
				</Grid>

				<Grid item xs={12} md={4}>
					<Paper sx={{ p: 3, position: 'sticky', top: 20 }}>
						<Stack spacing={2}>
							<Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
								<LocalBarIcon color="primary" />
								<Typography variant="h6" fontWeight={700}>
									Recipe Info
								</Typography>
							</Box>
							<Divider />
							{recipe.style && (
								<Box>
									<Typography variant="caption" color="text.secondary">
										Style
									</Typography>
									<Typography variant="body1" fontWeight={600}>
										{recipe.style}
									</Typography>
								</Box>
							)}
							{recipe.type && (
								<Box>
									<Typography variant="caption" color="text.secondary">
										Type
									</Typography>
									<Typography variant="body1" fontWeight={600}>
										{typeLabel}
									</Typography>
								</Box>
							)}
							<Divider />
							<Button
								variant="outlined"
								fullWidth
								startIcon={<ArrowBackIcon />}
								onClick={() => navigate('/recipes')}
							>
								Back to Recipes
							</Button>
						</Stack>
					</Paper>
				</Grid>
			</Grid>

			{recipe && (
				<AIChatOverlay
					title={`Recipe: ${recipe.name}`}
					subtitle="AI Assistant"
					historyKey={recipe.id}
					loadHistory={async () => {
						const userId = getUserId();
						const authHeader = getAuthHeader();
						if (!userId || !authHeader) return [];
						const { items = [] } = await AIService.getQuestionHistory(userId, authHeader, undefined, recipe.id);
						// Backend returns newest first; sort ascending so chat shows chronological order (oldest first).
						const sorted = [...items].sort((a, b) => {
							const ta = typeof a.createdAt === 'number' ? a.createdAt : new Date(a.createdAt).getTime();
							const tb = typeof b.createdAt === 'number' ? b.createdAt : new Date(b.createdAt).getTime();
							return ta - tb;
						});
						const loaded: ChatMessage[] = [];
						for (const item of sorted) {
							loaded.push({
								id: item.id,
								role: 'user',
								content: item.question,
								timestamp: new Date(typeof item.createdAt === 'number' ? item.createdAt : item.createdAt)
							});
							if (item.answer) {
								loaded.push({
									id: `${item.id}-reply`,
									role: 'assistant',
									content: item.answer,
									timestamp: new Date(typeof item.createdAt === 'number' ? item.createdAt : item.createdAt)
								});
							}
						}
						return loaded;
					}}
					onAsk={async (question) => {
						const userId = getUserId();
						const authHeader = getAuthHeader();
						if (!userId) throw new Error('Please log in again.');
						setCreditsLoading(true);
						try {
							const response: any = await AIService.askQuestionAboutRecipe(userId, question, recipe, authHeader);
							// Handle both direct { answer } and API Gateway-wrapped { body: string } shapes
							let answer: string | undefined = response?.answer;
							if (typeof answer !== 'string' && response?.body) {
								const parsed = typeof response.body === 'string' ? JSON.parse(response.body) : response.body;
								answer = parsed?.answer;
							}
							if (typeof answer !== 'string') throw new Error('Invalid response from AI');
							return answer;
						} finally {
							await loadCredits();
							setCreditsLoading(false);
						}
					}}
					suggestedQuestions={getSuggestedQuestionsForRecipe(recipe)}
					onLoginAgain={() => navigate('/login')}
					headerExtra={<CreditsDisplay variant="chip" size="small" />}
				/>
			)}
		</Container>
	);
};

export default RecipeDetailPage;
